/**
 * Get the RapidAPI key from localStorage
 * @returns {string|null}
 */
const getApiKey = () => {
  return localStorage.getItem('rapidapi_key');
};

/**
 * Set the RapidAPI key in localStorage
 * @param {string} apiKey
 */
export const setApiKey = (apiKey) => {
  localStorage.setItem('rapidapi_key', apiKey);
};

/**
 * Check if API key is configured
 * @returns {boolean}
 */
export const hasApiKey = () => {
  return !!getApiKey();
};

/**
 * Fetch the current price for a given symbol from Yahoo Finance via RapidAPI
 * @param {string} symbol - Stock/fund ticker symbol (e.g., 'AAPL', 'VWRL.L')
 * @param {string} apiKey - Optional API key (if not provided, uses stored key)
 * @returns {Promise<{price: number, currency: string, name: string}>}
 */
export const fetchStockPrice = async (symbol, apiKey = null) => {
  try {
    const key = apiKey || getApiKey();
    
    if (!key) {
      throw new Error('RapidAPI key is not configured. Please add your API key in the settings.');
    }

    console.log(`Fetching price for symbol: ${symbol}`);

    // Call RapidAPI Yahoo Finance Real Time API - using get-quote-summary endpoint
    const url = `https://yahoo-finance-real-time1.p.rapidapi.com/stock/get-quote-summary?symbol=${encodeURIComponent(symbol)}&lang=en-US&region=US`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'x-rapidapi-host': 'yahoo-finance-real-time1.p.rapidapi.com',
        'x-rapidapi-key': key
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid API key. Please check your RapidAPI key.');
      } else if (response.status === 404) {
        throw new Error(`Symbol not found: ${symbol}. Please check the symbol is correct.`);
      } else if (response.status === 429) {
        throw new Error('API rate limit exceeded. Please try again later.');
      }
      throw new Error(`API request failed with status: ${response.status}`);
    }

    const data = await response.json();

    // Navigate the nested structure: quoteSummary.result[0].price
    if (!data || !data.quoteSummary || !data.quoteSummary.result || data.quoteSummary.result.length === 0) {
      throw new Error(`No price data available for symbol: ${symbol}`);
    }

    const result = data.quoteSummary.result[0];
    const priceInfo = result.price;

    if (!priceInfo || !priceInfo.regularMarketPrice) {
      throw new Error(`No price data available for symbol: ${symbol}`);
    }

    // Get the price and currency from the nested structure
    const price = priceInfo.regularMarketPrice;
    const currency = priceInfo.currency || 'USD';
    const name = priceInfo.longName || priceInfo.shortName || symbol;

    console.log(`Successfully fetched price for ${symbol}: ${price} ${currency}`);

    return {
      price: parseFloat(price),
      currency: currency,
      name: name
    };

  } catch (error) {
    console.error(`Error fetching price for ${symbol}:`, error);
    throw new Error(`Failed to fetch price for ${symbol}: ${error.message}`);
  }
};

/**
 * Validate if a symbol exists and is tradable
 * @param {string} symbol - Stock/fund ticker symbol
 * @param {string} apiKey - Optional API key (if not provided, uses stored key)
 * @returns {Promise<boolean>}
 */
export const validateSymbol = async (symbol, apiKey = null) => {
  try {
    await fetchStockPrice(symbol, apiKey);
    return true;
  } catch (error) {
    console.error(`Symbol validation failed for ${symbol}:`, error);
    return false;
  }
};

/**
 * Fetch exchange rate for a currency pair from Yahoo Finance
 * @param {string} fromCurrency - Source currency code (e.g., 'GBP', 'USD')
 * @param {string} toCurrency - Target currency code (e.g., 'USD', 'EUR')
 * @param {string} apiKey - Optional API key (if not provided, uses stored key)
 * @returns {Promise<number>} Exchange rate (1 fromCurrency = X toCurrency)
 */
export const fetchExchangeRate = async (fromCurrency, toCurrency, apiKey = null) => {
  try {
    // Yahoo Finance uses forex symbols like GBPUSD=X for currency pairs
    const symbol = `${fromCurrency}${toCurrency}=X`;

    const priceData = await fetchStockPrice(symbol, apiKey);

    return priceData.price;
  } catch (error) {
    throw new Error(`Failed to fetch exchange rate ${fromCurrency}/${toCurrency}: ${error.message}`);
  }
};

/**
 * Fetch multiple exchange rates for a base currency
 * @param {string} baseCurrency - Base currency code (e.g., 'GBP')
 * @param {Array<string>} targetCurrencies - Array of target currency codes
 * @param {string} apiKey - Optional API key (if not provided, uses stored key)
 * @returns {Promise<Object>} Object with currency codes as keys and rates as values
 */
export const fetchMultipleExchangeRates = async (baseCurrency, targetCurrencies, apiKey = null) => {
  const rates = {};
  const errors = [];

  // Add base currency with rate 1.0
  rates[baseCurrency] = 1.0;

  // Fetch rates for all target currencies
  for (const targetCurrency of targetCurrencies) {
    if (targetCurrency === baseCurrency) {
      continue; // Skip if same as base
    }

    try {
      const rate = await fetchExchangeRate(baseCurrency, targetCurrency, apiKey);
      rates[targetCurrency] = rate;
    } catch (error) {
      console.error(`Failed to fetch rate for ${targetCurrency}:`, error);
      errors.push({ currency: targetCurrency, error: error.message });
    }
  }

  return { rates, errors };
};
